import { useState, useEffect } from 'react';
import { PRESENTER_SLIDE_NAMES } from './PresentationBannerConstants';

interface Props {
  notes: Record<number, string>;
  onClose: () => void;
  onExport: () => void;
}

export function NotesExportModal({ notes, onClose, onExport }: Props) {
  const [mounted, setMounted] = useState(false);
  const [animIn, setAnimIn]   = useState(false);

  const noteCount  = Object.values(notes).filter(n => n.trim().length > 0).length;
  const totalWords = Object.values(notes).join(' ').split(/\s+/).filter(Boolean).length;
  const readMin    = Math.max(1, Math.round(totalWords / 130));

  useEffect(() => {
    setMounted(true);
    const t = setTimeout(() => setAnimIn(true), 20);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setAnimIn(false); setTimeout(onClose, 300); }
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [onClose]);

  if (!mounted) return null;

  const handleClose = () => { setAnimIn(false); setTimeout(onClose, 300); };

  return (
    <div className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-all duration-300 ${animIn ? 'opacity-100' : 'opacity-0'}`}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-lg" onClick={handleClose} />
      <div className={`relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-3xl overflow-hidden shadow-2xl border border-slate-200 bg-slate-50 transition-all duration-300 ${animIn ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-printer-line text-sky-600 text-base"></i>
            </div>
            <div>
              <h3 className="text-sm font-extrabold text-slate-900">Notes Export Preview</h3>
              <p className="text-xs text-slate-500">{noteCount}/{PRESENTER_SLIDE_NAMES.length} slides with notes · ~{readMin} min read</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onExport} className="flex items-center gap-1.5 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold rounded-xl cursor-pointer transition-all whitespace-nowrap">
              <i className="ri-download-2-line text-xs"></i>Export PDF
            </button>
            <button onClick={handleClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center cursor-pointer transition-colors flex-shrink-0">
              <i className="ri-close-line text-slate-500 text-sm"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6" style={{ scrollbarWidth: 'thin' }}>
          {/* Doc header */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center">
                <i className="ri-slideshow-2-line text-white text-sm"></i>
              </div>
              <div>
                <p className="text-base font-extrabold text-slate-900">Presenter Notes</p>
                <p className="text-xs text-slate-400">openclaw.ai · {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
            </div>
            <div className="flex gap-5 pt-2 border-t border-slate-100">
              {[
                { icon: 'ri-layout-grid-line', label: `${PRESENTER_SLIDE_NAMES.length} slides`, color: 'text-slate-500' },
                { icon: 'ri-file-text-line',   label: `${noteCount} with notes`,                color: 'text-sky-500'   },
                { icon: 'ri-text',             label: `${totalWords} words`,                    color: 'text-slate-500' },
                { icon: 'ri-time-line',        label: `~${readMin} min read`,                   color: 'text-slate-500' },
              ].map(s => (
                <span key={s.label} className={`flex items-center gap-1.5 text-xs font-medium ${s.color}`}>
                  <i className={`${s.icon} text-sm`}></i>{s.label}
                </span>
              ))}
            </div>
          </div>

          {/* Slide entries */}
          <div className="space-y-2.5">
            {PRESENTER_SLIDE_NAMES.map((name, i) => {
              const note   = notes[i] ?? '';
              const hasNote = note.trim().length > 0;
              const wc     = note.split(/\s+/).filter(Boolean).length;
              return (
                <div key={name} className={`rounded-2xl border-2 overflow-hidden ${hasNote ? 'border-sky-200 bg-white' : 'border-slate-100 bg-slate-50/60'}`}>
                  <div className={`flex items-center gap-3 px-4 py-3 ${hasNote ? 'bg-sky-50 border-b border-sky-100' : ''}`}>
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-extrabold ${hasNote ? 'bg-sky-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                      {i + 1}
                    </div>
                    <span className={`text-sm font-bold ${hasNote ? 'text-slate-900' : 'text-slate-400'}`}>{name}</span>
                    {!hasNote && <span className="ml-auto text-xs text-slate-400 italic">No notes for this slide</span>}
                    {hasNote  && <span className="ml-auto flex items-center gap-1 text-xs text-sky-600 font-semibold whitespace-nowrap"><i className="ri-check-line text-xs"></i>{wc} word{wc !== 1 ? 's' : ''}</span>}
                  </div>
                  {hasNote && <div className="px-4 py-3"><p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{note}</p></div>}
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-4 border-t border-slate-200 text-center">
            <p className="text-xs text-slate-400">Generated by openclaw.ai · Presenter Notes Export · {new Date().getFullYear()}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
